import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Service, ServiceCategory } from '@/types';
import { useTemplate } from '@/contexts/TemplateContext';
import { formatCurrencyAmount, getServicePriceForCurrency } from '@/utils/currency';

interface ServiceSelectionProps {
  services: Service[];
  categories: ServiceCategory[];
  selectedServices: Service[];
  onServicesChange: (services: Service[]) => void;
  totalDuration: number;
  totalPrice: number;
  currency?: string;
  showLocationLabels?: boolean;
}

export const ServiceSelection: React.FC<ServiceSelectionProps> = ({
  services,
  categories,
  selectedServices,
  onServicesChange,
  totalDuration,
  totalPrice,
  currency = 'EUR',
  showLocationLabels = false,
}) => {
  const { colors } = useTemplate();
  const { t } = useTranslation('booking');
  const isSelected = (serviceId: string) =>
    selectedServices.some((s) => s.id === serviceId);

  const toggleService = (service: Service) => {
    if (isSelected(service.id)) {
      onServicesChange(selectedServices.filter((s) => s.id !== service.id));
    } else {
      onServicesChange([...selectedServices, service]);
    }
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return t('service.durationVaries');
    return t('service.duration', { minutes });
  };

  const formatPrice = (service: Service, display: string) => {
    const price = getServicePriceForCurrency(service, currency);
    if (display === 'hidden') return '';
    if (display === 'contact') return t('service.contactForPrice');
    if (display === 'from' && price != null) {
      return t('service.fromPrice', { price: formatCurrencyAmount(price, currency) });
    }
    if (price === null) return '';
    return formatCurrencyAmount(price, currency);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">{t('service.title')}</h2>
        <p className="text-gray-600 mt-2">{t('service.subtitle')}</p>
      </div>

      {/* Services by Category */}
      <div className="space-y-6">
        {categories.map((category) => {
          const categoryServices = services.filter(
            (s) => s.category_id === category.id
          );
          if (categoryServices.length === 0) return null;

          return (
            <div key={category.id} className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="font-semibold text-lg mb-3 text-gray-800">
                {category.name}
              </h3>
              <div className="space-y-2">
                {categoryServices.map((service) => (
                  <label
                    key={service.id}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected(service.id)
                        ? 'border-current'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={isSelected(service.id) ? { borderColor: colors.colors.primary, backgroundColor: `${colors.colors.primary}10` } : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected(service.id)}
                      onChange={() => toggleService(service)}
                      className="w-5 h-5 rounded focus:ring-2"
                      style={{ accentColor: colors.colors.primary }}
                    />
                    <div className="ml-3 flex-1">
                      <div className="font-medium text-gray-900">
                        {service.name}
                        {showLocationLabels && service.profile?.business_name && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {service.profile.business_name}
                          </span>
                        )}
                        {service.is_multi_booking && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            🎥 Multi-booking
                          </span>
                        )}
                        {service.is_multi_booking && service.max_capacity && (
                          <span className="ml-1 text-xs text-blue-600">
                            (Max {service.max_capacity})
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDuration(service.duration_minutes)}
                        {service.price_display !== 'hidden' &&
                          getServicePriceForCurrency(service, currency) != null && (
                          <span className="ml-2">
                            {formatPrice(service, service.price_display)}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          );
        })}

        {/* Uncategorized services */}
        {services.filter((s) => !s.category_id).length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h3 className="font-semibold text-lg mb-3 text-gray-800">{t('service.otherServices')}</h3>
            <div className="space-y-2">
              {services
                .filter((s) => !s.category_id)
                .map((service) => (
                  <label
                    key={service.id}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected(service.id)
                        ? 'border-current'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={isSelected(service.id) ? { borderColor: colors.colors.primary, backgroundColor: `${colors.colors.primary}10` } : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected(service.id)}
                      onChange={() => toggleService(service)}
                      className="w-5 h-5 rounded focus:ring-2"
                      style={{ accentColor: colors.colors.primary }}
                    />
                    <div className="ml-3 flex-1">
                      <div className="font-medium text-gray-900">{service.name}</div>
                      <div className="text-sm text-gray-500">
                        {formatDuration(service.duration_minutes)}
                      </div>
                    </div>
                  </label>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      {selectedServices.length > 0 && (
        <div className="rounded-lg p-4 mt-6" style={{ backgroundColor: `${colors.colors.primary}10` }}>
          <div className="flex justify-between items-center">
            <div>
              <span className="font-medium" style={{ color: colors.colors.text }}>
                {t('service.servicesSelected', { count: selectedServices.length })}
              </span>
              <span className="mx-2" style={{ color: colors.colors.textMuted }}>•</span>
              <span style={{ color: colors.colors.textMuted }}>{t('service.totalDuration', { minutes: totalDuration })}</span>
            </div>
            {totalPrice > 0 && (
              <span className="text-xl font-bold" style={{ color: colors.colors.text }}>
                {formatCurrencyAmount(totalPrice, currency)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
